/**
 * Client Dashboard permission constants and helpers.
 *
 * Backend should issue these CASL abilities in userData.abilities on login:
 * - { action: 'read', subject: 'client_dashboard' } — dashboard access, metrics, pool (read-only), reports
 * - { action: 'manage', subject: 'client_dashboard' } — admin settings (e.g. document check); implies read
 * - { action: 'manage', subject: 'candidates' } — assign recruiter/POC in dashboard; bank edits (with update)
 * - { action: 'update', subject: 'candidates' } — upload bank proof / edit bank details
 * - { action: 'read', subject: 'generate_offer' } — view / preview existing offer letters
 * - { action: 'create', subject: 'generate_offer' } — generate / regenerate / download / email offers
 * - { action: 'manage', subject: 'generate_offer' } — full offer-letter workflow
 *
 * `read candidates` is NOT a substitute for `read client_dashboard` on dashboard routes/nav.
 * Candidate pool viewing requires client_dashboard read (or manage client_dashboard / manage all).
 * Candidate mutations in the dashboard require manage candidates (or manage client_dashboard / manage all).
 */

export const CLIENT_DASHBOARD_PERMISSIONS = {
  MODULE: 'client_dashboard',
  CANDIDATES: 'candidates',
  GENERATE_OFFER: 'generate_offer',
}

export const CLIENT_DASHBOARD_ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  MANAGE: 'manage',
}

const MODULE = CLIENT_DASHBOARD_PERMISSIONS.MODULE
const CANDIDATES = CLIENT_DASHBOARD_PERMISSIONS.CANDIDATES
const GENERATE_OFFER = CLIENT_DASHBOARD_PERMISSIONS.GENERATE_OFFER

function hasPermission(canFn, action, subject) {
  if (typeof canFn !== 'function') return false
  return !!canFn(action, subject)
}

function hasOfferMutateCompat(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, 'all')
  )
}

/** Route, nav, selection page, and dashboard shell access. */
export function canAccessClientDashboard(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.READ, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, 'all')
  )
}

/** View candidate pool, metrics, filters, and reports (requires client_dashboard, not read candidates alone). */
export function canViewClientDashboardCandidates(canFn) {
  return canAccessClientDashboard(canFn)
}

/** Assign recruiter / POC (bulk or single). */
export function canAssignClientDashboardCandidates(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, 'all')
  )
}

/**
 * View / preview an existing offer letter PDF.
 * Read-only dashboard users can still open generated letters.
 */
export function canViewOfferLetter(canFn) {
  return (
    canAccessClientDashboard(canFn)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.READ, GENERATE_OFFER)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.CREATE, GENERATE_OFFER)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, GENERATE_OFFER)
    || hasOfferMutateCompat(canFn)
  )
}

/**
 * Generate or regenerate offer letters.
 * Requires create/manage on generate_offer (read alone is view-only).
 */
export function canGenerateOffer(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.CREATE, GENERATE_OFFER)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, GENERATE_OFFER)
    // Backward compat: users who had full candidate manage before generate_offer existed
    || hasOfferMutateCompat(canFn)
  )
}

/** Download offer letter PDF (same bar as generate). */
export function canDownloadOfferLetter(canFn) {
  return canGenerateOffer(canFn)
}

/** Send / resend offer letter email (same bar as generate). */
export function canSendOfferEmail(canFn) {
  return canGenerateOffer(canFn)
}

/** View bank account number / IFSC (sensitive — not granted to bare dashboard read). */
export function canViewClientDashboardBankDetails(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.UPDATE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, 'all')
    || canGenerateOffer(canFn)
  )
}

/** Upload bank proof / account details from the dashboard. */
export function canManageClientDashboardBankDetails(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.UPDATE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, CANDIDATES)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || canGenerateOffer(canFn)
  )
}

/** Admin-only dashboard settings (document check toggle). */
export function canManageClientDashboardSettings(canFn) {
  return (
    hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, MODULE)
    || hasPermission(canFn, CLIENT_DASHBOARD_ACTIONS.MANAGE, 'all')
  )
}

/** Subject for route/nav resource checks (client_dashboard only — not read candidates). */
export const CLIENT_DASHBOARD_ROUTE_RESOURCES = [MODULE]

export default {
  CLIENT_DASHBOARD_PERMISSIONS,
  CLIENT_DASHBOARD_ACTIONS,
  CLIENT_DASHBOARD_ROUTE_RESOURCES,
  canAccessClientDashboard,
  canViewClientDashboardCandidates,
  canAssignClientDashboardCandidates,
  canViewOfferLetter,
  canGenerateOffer,
  canDownloadOfferLetter,
  canSendOfferEmail,
  canViewClientDashboardBankDetails,
  canManageClientDashboardBankDetails,
  canManageClientDashboardSettings,
}
